import Phaser from "phaser";
import { DEFAULT_MODAL_TIMING, type ModalTiming } from "./tokens.js";

/** 점이 사각형(x,y,w,h) 안(경계 포함)인지 — dim 닫기 제외 hit-test(순수, 테스트용 export). */
export function pointInRect(
  px: number,
  py: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number,
): boolean {
  return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
}

/**
 * `ModalLayer` 라이프사이클 이벤트 키.
 * - `Opened`: 등장 tween onComplete 시 1회 emit. 단, `close()`가 등장 중 호출되면 emit 안 됨.
 * - `Closed`: 퇴장 tween onComplete 시 emit. emit 직후 `destroy()` 호출.
 */
export const MODAL_EVENT = {
  Opened: "modal:opened",
  Closed: "modal:closed",
} as const;

export interface ModalLayerOptions {
  /** 화면 너비 — overlay 사이즈 + scale pivot 계산. */
  readonly screenWidth: number;
  /** 화면 높이 — overlay 사이즈 + scale pivot 계산. */
  readonly screenHeight: number;
  /** overlay 색상 hex (default 0x000000). */
  readonly overlayColor?: number;
  /** overlay alpha (default 0.65). */
  readonly overlayAlpha?: number;
  /** depth (default 1000). */
  readonly depth?: number;
  /** entrance/exit tween 타이밍 override — 미지정 시 DEFAULT_MODAL_TIMING. */
  readonly modalTiming?: ModalTiming;
  /**
   * overlay가 배경 클릭을 가로챌지 여부 (default true). false면 overlay를 interactive로 만들지 않아
   * 입력이 통과한다 — 중첩 컨테이너 + 콘텐츠 버튼이 overlay 입력에 가로채여 무반응이 되는 케이스를
   * 회피할 때 사용 (호출자가 phase 가드 등으로 배경 클릭을 이미 차단하는 경우). 시각 dim은 유지.
   */
  readonly blockBackgroundInput?: boolean;
  /**
   * dim(overlay/배경) 클릭 시 호출되는 콜백. 지정하면 배경 탭에 도메인 동작(예: 호스트의 close+참조정리)을 연결한다.
   * `blockBackgroundInput`이 false면 overlay가 non-interactive라 호출되지 않는다(시각 dim만 유지).
   * **지정 시 `closeOnBackdrop` default 닫기는 타지 않는다** — 콜백이 닫기 책임을 가져간다.
   */
  readonly onBackdropClick?: () => void;
  /**
   * dim(배경) 클릭 시 자기 자신을 닫을지 (default true). `onBackdropClick` 미지정일 때만 적용 —
   * 미지정 + true면 배경 탭으로 `this.close()`를 호출(모든 모달 공통 "배경 눌러 닫기"). false면 배경 탭을
   * 차단만 하고 닫지 않는다(보상 강제 확인 등 닫힘 방지가 필요한 모달의 opt-out). `setBackdropCloseExclusion`으로
   * 등록한 패널 영역 안의 클릭은 이 경우에도 닫지 않는다(팝업 밖 dim만 닫힘).
   */
  readonly closeOnBackdrop?: boolean;
}

/**
 * 반투명 overlay + entrance/exit scale tween + emit(Opened/Closed) — 추상 베이스.
 *
 * 본문 콘텐츠는 서브클래스가 `contentContainer`에 직접 추가 (패널/제목/닫기 버튼 등 도메인 요소).
 *
 * 등장: `setAlpha(0)`/`setScale(enterFromScale)` → tween scale→1 + alpha→1 + position 보정. onComplete에 `emit(Opened)`.
 * 퇴장: `close()` 호출 시 tween → `emit(Closed) + destroy()`. idempotent (`closing` 가드 — 중복 호출 ignore).
 *
 * **pivot 보정**: Container scale은 origin(0,0) 기준이라 자식 절대좌표가 좌상단으로 쏠림 — `position`을
 * `((1-s)*pivotX, (1-s)*pivotY)` (pivot = 화면 중앙)으로 함께 이동해 균등 축소되는 효과를 만든다.
 */
export abstract class ModalLayer extends Phaser.GameObjects.Container {
  protected readonly contentContainer: Phaser.GameObjects.Container;
  /** viewport resize 시 `onResize`로 갱신 — getter로 외부 노출(`screenWidth`). */
  private _screenWidth: number;
  private _screenHeight: number;
  /** overlay 사이즈를 `onResize`에서 재설정하기 위한 참조. */
  private readonly overlay: Phaser.GameObjects.Rectangle;
  private readonly modalTiming: ModalTiming;
  private closing = false;
  /** dim 클릭 닫기 시 제외할 콘텐츠(패널) 영역(화면 좌표) — 미설정 시 overlay 전체가 닫기 영역. */
  private backdropExclusion?: { x: number; y: number; w: number; h: number };

  protected get screenWidth(): number {
    return this._screenWidth;
  }
  protected get screenHeight(): number {
    return this._screenHeight;
  }

  constructor(scene: Phaser.Scene, options: ModalLayerOptions) {
    super(scene, 0, 0);
    this._screenWidth = options.screenWidth;
    this._screenHeight = options.screenHeight;
    this.modalTiming = options.modalTiming ?? DEFAULT_MODAL_TIMING;
    // 2026-05-26: default depth 1000 → 2000 (사용자 보고 "팝업 뒤 UI 튀어나옴" 차단).
    // 메인뷰 typewriter overlay setDepth(5000)와는 동시 활성 X (typewriter complete → ChoiceList → 결과 popup 순서).
    this.setDepth(options.depth ?? 2000);

    this.overlay = scene.add
      .rectangle(
        0,
        0,
        this._screenWidth,
        this._screenHeight,
        options.overlayColor ?? 0x000000,
        options.overlayAlpha ?? 0.65,
      )
      .setOrigin(0, 0);
    this.overlay.name = "modalOverlay"; // dev QA hit-test 식별용
    // ⚠ overlay interactive는 배경 클릭 차단용이나, 중첩 컨테이너 + setDepth 입력 정렬에서
    //   overlay가 콘텐츠 버튼 클릭을 가로채 "버튼 무반응"을 유발할 수 있다. blockBackgroundInput=false면
    //   overlay를 non-interactive로 둬 입력이 버튼에 도달하게 한다(시각 dim은 유지). 배경 보호는 호출자 책임.
    if (options.blockBackgroundInput ?? true) {
      this.overlay.setInteractive();
      this.overlay.on("pointerdown", (p: Phaser.Input.Pointer) => {
        // 배경 클릭 차단 + backdrop 닫기. 콘텐츠(패널) 영역 안의 클릭은 어느 경로든 닫지 않는다(팝업 밖 dim만 닫힘).
        const ex = this.backdropExclusion;
        if (ex && pointInRect(p.x, p.y, ex.x, ex.y, ex.w, ex.h)) return;
        // 명시 콜백이 있으면 그것이 닫기 책임(호스트 close+참조정리). 없으면 closeOnBackdrop(default true)에서 자기 자신 닫기.
        const cb = options.onBackdropClick;
        if (cb) {
          cb();
          return;
        }
        if (options.closeOnBackdrop ?? true) this.close();
      });
    }
    this.add(this.overlay);

    this.contentContainer = scene.add.container(0, 0);
    this.add(this.contentContainer);
  }

  /**
   * dim 클릭 닫기 시 제외할 콘텐츠(패널) 영역을 화면 좌표로 등록한다 — 이 사각형 안의 클릭은 닫지 않는다.
   * 서브클래스가 패널을 만든 뒤 호출(미설정 시 overlay 전체가 닫기 영역). `onBackdropClick`과 함께 사용.
   */
  protected setBackdropCloseExclusion(x: number, y: number, width: number, height: number): void {
    this.backdropExclusion = { x, y, w: width, h: height };
  }

  /**
   * viewport resize 응답 hook — overlay 사이즈 재설정 + pivot 보정 좌표 재계산.
   *
   * 호출자가 `scale.on('resize')` 등에서 직접 호출. 패키지 측 자동 구독하지 않음 — 다른 프로젝트에서 재사용 시
   * scene.scale 의존을 강제하지 않기 위해 host(Scene/CompositionRoot) 책임으로 둠.
   *
   * 서브클래스가 override할 때 `super.onResize(w, h)` 호출 후 자체 자식 layout 갱신을 추가.
   *
   * Tween 진행 중에 호출되면 pivot 보정 좌표가 다음 frame부터 새 viewport 기준으로 적용된다.
   * Tween 완료 후엔 `setPosition(0, 0)` 상태라 추가 보정 불필요.
   */
  public onResize(screenWidth: number, screenHeight: number): void {
    this._screenWidth = screenWidth;
    this._screenHeight = screenHeight;
    this.overlay.setSize(screenWidth, screenHeight);
    // tween 미진행 시(open 완료 또는 close 전): position (0,0)이라 보정 불필요.
    // tween 진행 시: 다음 tween targets 갱신 — 진행 중 tween은 viewport 변경에 대해 사용자 경험상 유의미한 보정 어렵다 (보통 모바일 회전은 modal 닫힘 상태).
  }

  /**
   * 등장 트리거 — visible on + alpha 0/scale enterFromScale → tween으로 alpha 1/scale 1 복귀.
   * pivot 보정으로 화면 중앙 기준 균등 축소 효과. onComplete 시 `MODAL_EVENT.Opened` emit.
   * 호출 전에 `scene.add.existing(modal)` 등록 + contentContainer에 도메인 컴포넌트 add 완료 가정.
   */
  open(): void {
    this.setVisible(true);
    this.setAlpha(0);
    const fromScale = this.modalTiming.enterFromScale;
    const pivotX = this.screenWidth / 2;
    const pivotY = this.screenHeight / 2;
    this.setScale(fromScale);
    this.setPosition((1 - fromScale) * pivotX, (1 - fromScale) * pivotY);
    this.scene.tweens.add({
      targets: this,
      alpha: 1,
      scale: 1,
      x: 0,
      y: 0,
      duration: this.modalTiming.enterDurationMs,
      ease: this.modalTiming.enterEase,
      onComplete: () => {
        if (this.closing) return;
        this.emit(MODAL_EVENT.Opened);
      },
    });
  }

  /**
   * 퇴장 트리거 — alpha 1→0 + scale 1→exitToScale tween 후 `MODAL_EVENT.Closed` emit + `destroy()`.
   * idempotent: `closing` 가드로 중복 호출 무시 (같은 모달 인스턴스에서 close 여러 번 호출 안전).
   */
  close(): void {
    if (this.closing) return;
    this.closing = true;
    this.scene.tweens.killTweensOf(this);
    const toScale = this.modalTiming.exitToScale;
    const pivotX = this.screenWidth / 2;
    const pivotY = this.screenHeight / 2;
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scale: toScale,
      x: (1 - toScale) * pivotX,
      y: (1 - toScale) * pivotY,
      duration: this.modalTiming.exitDurationMs,
      ease: this.modalTiming.exitEase,
      onComplete: () => {
        this.emit(MODAL_EVENT.Closed);
        this.destroy();
      },
    });
  }
}
